import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { MainLayout } from "components/layout/MainLayout";
import { Button } from "components/ui";
import { ROUTES } from "constants/index";
import { useAuth } from "hooks/useAuth";
import { packagesService } from "services/packages.service";
import { createSession } from "services/payment.service";
import { useToast } from "hooks/useToast";
import { Package, getPriceByDuration, getDetailByDuration, isDemoPackage } from "types/package";
import parse, {
  domToReact,
  HTMLReactParserOptions,
  Element,
  DOMNode,
} from "html-react-parser";

const CHECKOUT_SCRIPT_URL =
  "https://creditlibanais-netcommerce.gateway.mastercard.com/static/checkout/checkout.min.js";

function loadCheckoutScript(): Promise<void> {
  if (typeof (window as any).Checkout !== "undefined") {
    return Promise.resolve();
  }
  const existing = document.querySelector(`script[src="${CHECKOUT_SCRIPT_URL}"]`);
  if (existing) {
    return new Promise((resolve) => {
      const check = () => {
        if (typeof (window as any).Checkout !== "undefined") resolve();
        else setTimeout(check, 50);
      };
      check();
    });
  }
  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = CHECKOUT_SCRIPT_URL;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Failed to load payment script"));
    document.body.appendChild(script);
  });
}

const parserOptions: HTMLReactParserOptions = {
  replace: (domNode: DOMNode) => {
    if (domNode instanceof Element) {
      if (domNode.name === "ul") {
        return (
          <ul className="list-disc pl-5">
            {domToReact(domNode.children as DOMNode[], parserOptions)}
          </ul>
        );
      }
      if (domNode.name === "ol") {
        return (
          <ol className="list-decimal pl-5">
            {domToReact(domNode.children as DOMNode[], parserOptions)}
          </ol>
        );
      }
    }
    return undefined;
  },
};

export const UpgradePackagePage: React.FC = () => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [packages, setPackages] = useState<Package[]>([]);
  const [loading, setLoading] = useState(true);
  const [isYearly, setIsYearly] = useState(true);
  const [proceedingId, setProceedingId] = useState<string | null>(null);

  const isCompanyAccount = useMemo(
    () => Boolean(user && user.roleId && Number(user.roleId) !== 1),
    [user]
  );

  const filteredPackages = useMemo(() => {
    return packages.filter(
      (p) =>
        !isDemoPackage(p) &&
        p.isIndividual === !isCompanyAccount
    );
  }, [packages, isCompanyAccount]);

  const getPrice = (pkg: Package) => getPriceByDuration(pkg, isYearly);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    packagesService
      .getAll()
      .then((data) => {
        if (!cancelled) setPackages(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!cancelled) {
          setPackages([]);
          toast.error("Failed to load packages");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []); // Run once on mount; toast intentionally omitted to avoid effect re-runs

  useEffect(() => {
    if (!isAuthenticated) {
      navigate(ROUTES.LOGIN);
      return;
    }
  }, [isAuthenticated, navigate]);

  const handleProceed = async (pkg: Package) => {
    if (!user?.id) {
      toast.error("Please sign in to continue.");
      return;
    }
    const amount = getPrice(pkg);
    setProceedingId(String(pkg.id));
    try {
      await loadCheckoutScript();

      const detail = getDetailByDuration(pkg, isYearly);
      const res = await createSession({
        amount,
        userId: Number(user.id),
        companyId: user.company_id ? Number(user.company_id) : null,
        packageDetailsId: detail ? Number(detail.id) : undefined,
      });

      let sessionData: any;
      const resAny = res as any;
      if (resAny && typeof resAny === "object") {
        if (resAny.data !== undefined) {
          const responseData = resAny.data;
          if (typeof responseData === "string") {
            try {
              sessionData = JSON.parse(responseData);
            } catch {
              sessionData = responseData;
            }
          } else {
            sessionData = responseData;
          }
        } else if (resAny.session) {
          sessionData = resAny;
        } else {
          sessionData = resAny;
        }
      } else {
        sessionData = resAny;
      }

      const sessionId = sessionData?.session?.id;
      if (!sessionId) {
        toast.error("Failed to get payment session");
        return;
      }

      localStorage.setItem("mpgs_session_id", sessionId);
      (window as any).Checkout.configure({
        session: { id: sessionId },
      });
      (window as any).Checkout.showPaymentPage();
    } catch (e: any) {
      toast.error(e?.message || "Failed to start payment");
    } finally {
      setProceedingId(null);
    }
  };

  if (!isAuthenticated) return null;

  return (
    <MainLayout>
      <div className="mx-auto max-w-6xl px-4 py-8">
        <h1 className="text-2xl font-bold text-secondary-900 mb-2">
          Upgrade package
        </h1>
        <p className="text-secondary-600 mb-6">
          Choose a package for your {isCompanyAccount ? "company" : "individual"} account.
        </p>

        {loading ? (
          <div className="py-12 text-center text-secondary-500">
            Loading packages…
          </div>
        ) : filteredPackages.length === 0 ? (
          <div className="py-12 text-center text-secondary-600 rounded-xl border border-secondary-200 bg-secondary-50">
            <p className="font-medium">No packages available</p>
            <p className="text-sm mt-1">
              There are no {isCompanyAccount ? "company" : "individual"} packages to show at the moment. Please try again later or contact support.
            </p>
          </div>
        ) : (
          <>
            <div className="flex justify-center mb-6">
              <div className="inline-flex rounded-full border border-secondary-300 bg-white p-1">
                <button
                  type="button"
                  onClick={() => setIsYearly(false)}
                  className={`px-4 py-2 rounded-full text-sm font-medium ${
                    !isYearly ? "bg-primary-600 text-white" : "text-secondary-800"
                  }`}
                >
                  Monthly
                </button>
                <button
                  type="button"
                  onClick={() => setIsYearly(true)}
                  className={`px-4 py-2 rounded-full text-sm font-medium ${
                    isYearly ? "bg-primary-600 text-white" : "text-secondary-800"
                  }`}
                >
                  Yearly
                </button>
              </div>
            </div>

            <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
              {filteredPackages.map((pkg) => {
                const price = getPrice(pkg);
                const isProceeding = proceedingId === String(pkg.id);
                const isPro = pkg.name === "Pro";
                return (
                  <div
                    key={pkg.id}
                    className={`rounded-2xl border shadow-sm flex flex-col ${
                      isPro
                        ? "bg-gradient-to-b from-white to-primary-50 border-primary-200"
                        : "bg-gradient-to-b from-white to-secondary-50 border-secondary-200"
                    }`}
                  >
                    <div className="p-5 flex flex-col flex-1">
                      <h3 className="text-lg font-bold text-secondary-900 py-1">
                        {pkg.title || pkg.name}
                      </h3>
                      <p className="mt-2 text-3xl font-bold text-secondary-900">
                        ${price}
                        <span className="text-base font-normal text-secondary-600">
                          {isYearly ? " /yr" : " /mo"}
                        </span>
                      </p>
                      <div className="mt-4 text-secondary-600 text-sm flex-1">
                        {pkg.description
                          ? parse(pkg.description, parserOptions)
                          : "—"}
                      </div>
                      <Button
                        onClick={() => handleProceed(pkg)}
                        disabled={!!proceedingId}
                        className="w-full mt-6"
                      >
                        {isProceeding
                          ? "Opening payment…"
                          : "Proceed to payment"}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </MainLayout>
  );
};

export default UpgradePackagePage;
